/* eslint-disable object-curly-newline */
import { TableCell, TableHead, TableSortLabel } from '@mui/material';
import React, { FC } from 'react';
import { IAssetStatus } from '../../../api/vault/assetStatus/interfaces';
import { defaultOrder, JobTab, Order } from '../../../state/jobStatus';
import { StyledTableRowHeader } from './JobListTableHeaderStyles';

interface IHeadCell {
  id: string;
  label: string;
  exchangeOnly?: boolean;
  class?: string;
  tabs?: JobTab[];
  canSort?: boolean;
}

const headCells: IHeadCell[] = [
  { id: 'actionStatus', label: 'Status', class: 'actionStatus', canSort: true },
  { id: 'actionType', label: 'Action', class: 'action', canSort: true },
  { id: 'user', label: 'User', class: 'user', canSort: true },
  { id: 'assetName', label: 'Asset Title', canSort: true },
  { id: 'exportFileName', label: 'Export File Name', exchangeOnly: true, canSort: true, tabs: [JobTab.Download, JobTab.Upload] },
  { id: 'exchangeSport', label: 'Exchange Sport', class: 'exchangeSport', tabs: [JobTab.ExchangeCreate] },
  { id: 'actionDate', label: 'Time', class: 'time', canSort: true },
  { id: 'actionProgress', label: 'Progress', class: 'progress', canSort: true },
  { id: 'actionMachine', label: 'Machine', class: 'machine', tabs: [JobTab.Upload, JobTab.Download], canSort: true },
];

const liveEventHeadCells: IHeadCell[] = [
  { id: 'eventTitle', label: 'Event Title', class: 'eventTitle', canSort: true },
  { id: 'streamUrl', label: 'Stream', class: 'streamUrl' },
  { id: 'securityProfiles', label: 'Security Profiles', class: 'securityProfiles' },
  { id: 'start', label: 'Start (EDT)', class: 'start', canSort: true },
  { id: 'duration', label: 'Duration', class: 'duration', canSort: true },
  { id: 'streamStatus', label: 'Status', class: 'streamStatus', canSort: true },
  { id: 'clip', label: 'Clip', class: 'clip' },
  { id: 'actions', label: 'Actions', class: 'liveActions' },
];

interface IProps {
  jobTab: JobTab,
  isExchange: boolean,
  updateSortingOrder: (orderBy: keyof IAssetStatus, order: Order) => void,
  order: Order,
  orderBy: keyof IAssetStatus,
  // Live Events
  updateLiveEventsSortingOrder: (orderBy: string, order: Order) => void,
  liveEventsOrder: Order,
  liveEventsOrderBy: string,
}

const JobListTableHeader: FC<IProps> = (props: IProps) => {
  const {
    isExchange,
    order,
    orderBy,
    updateSortingOrder,
    updateLiveEventsSortingOrder,
    liveEventsOrder,
    liveEventsOrderBy,
    jobTab,
  } = props;

  if (jobTab === JobTab.LiveEvents || jobTab === JobTab.ExchangeEvents) {
    return (
      <TableHead>
        <StyledTableRowHeader>
          {liveEventHeadCells.map((headCell) => {
            if (!headCell.canSort) {
              return (
                <TableCell
                  key={headCell.id}
                  align="left"
                  className={headCell.class ? headCell.class : ''}
                >
                  {headCell.label}
                </TableCell>
              );
            }
            return (
              <TableCell
                key={headCell.id}
                align="left"
                sortDirection={(liveEventsOrderBy === headCell.id) ? liveEventsOrder : false}
                className={headCell.class ? headCell.class : ''}
              >
                <TableSortLabel
                  active={liveEventsOrderBy === headCell.id}
                  direction={liveEventsOrderBy === headCell.id ? liveEventsOrder : 'asc'}
                  onClick={() => {
                    let setOrder = defaultOrder;
                    if (liveEventsOrderBy === headCell.id) setOrder = (liveEventsOrder === 'asc' ? 'desc' : 'asc');
                    updateLiveEventsSortingOrder(headCell.id, setOrder);
                  }}
                >
                  {headCell.label}
                </TableSortLabel>
              </TableCell>
            );
          })}
        </StyledTableRowHeader>
      </TableHead>
    );
  }

  const cells = headCells.filter(({ exchangeOnly, tabs }) => {
    let showCell = true;
    if (tabs && tabs.length > 0 && jobTab && !tabs.includes(jobTab)) showCell = false;
    if (!isExchange && exchangeOnly) showCell = false;
    return showCell;
  }).map((headCell) => (jobTab === JobTab.Publish && headCell.id === 'actionType' ? { ...headCell, label: 'Destination' } : headCell));
  return (
    <TableHead>
      <StyledTableRowHeader>
        {cells.map((headCell) => {
          if (!headCell.canSort) {
            return (
              <TableCell
                key={headCell.id}
                align="left"
                className={headCell.class ? headCell.class : ''}
              >
                {headCell.label}
              </TableCell>
            );
          }
          return (
            <TableCell
              key={headCell.id}
              align="left"
              sortDirection={orderBy === headCell.id ? order : false}
              className={headCell.class ? headCell.class : ''}
            >
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={() => {
                  let setOrder = defaultOrder;
                  if (orderBy === headCell.id) setOrder = (order === 'asc' ? 'desc' : 'asc');
                  updateSortingOrder(headCell.id as keyof IAssetStatus, setOrder);
                }}
              >
                {headCell.label}
              </TableSortLabel>
            </TableCell>
          );
        })}
        <TableCell
          align="left"
          className={isExchange ? 'actionButtons exchange' : 'actionButtons'}
        />
      </StyledTableRowHeader>
    </TableHead>
  );
};

export default JobListTableHeader;
